import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Avatar,
  Badge,
  List,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  Divider,
  CircularProgress,
  useTheme,
} from '@mui/material';
import { Send } from '@mui/icons-material';
import {
  useGetConversationsQuery,
  useGetConversationQuery,
  useSendMessageMutation,
} from 'state/api';
import { PageContainer } from 'components/shared';

const getInitials = (name = '') =>
  name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((n) => n[0]?.toUpperCase())
    .join('') || '?';

const formatTime = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString([], {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const MessageView = () => {
  const theme = useTheme();
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [body, setBody] = useState('');

  const { data: listData, isLoading: listLoading } = useGetConversationsQuery({ search });
  const { data: convData, isFetching: convLoading } = useGetConversationQuery(selectedId, {
    skip: !selectedId,
  });
  const [sendMessage, { isLoading: sending }] = useSendMessageMutation();

  const conversations = listData?.data ?? [];
  const conversation = convData?.data ?? null;
  const messages = conversation?.messages ?? [];

  const handleSend = async () => {
    const text = body.trim();
    if (!text || !selectedId) return;
    try {
      await sendMessage({ id: selectedId, body: text }).unwrap();
      setBody('');
    } catch (err) {
      // preserve input on failure
    }
  };

  return (
    <PageContainer title="Messages" subtitle="Chat with your customers" maxWidth={900}>
      {!selectedId ? (
        <Paper sx={{ bgcolor: theme.palette.background.alt }}>
          <Box p={1.5}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search conversations..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </Box>
          <Divider />
          {listLoading ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress size={28} />
            </Box>
          ) : conversations.length === 0 ? (
            <Typography variant="body2" color="text.secondary" align="center" p={4}>
              No conversations found.
            </Typography>
          ) : (
            <List disablePadding>
              {conversations.map((c) => (
                <ListItemButton
                  key={c?._id}
                  onClick={() => setSelectedId(c?._id)}
                  alignItems="flex-start"
                >
                  <ListItemAvatar>
                    <Badge
                      color="error"
                      badgeContent={c?.unread || 0}
                      invisible={!c?.unread}
                      overlap="circular"
                    >
                      <Avatar src={c?.avatar || undefined}>{getInitials(c?.name)}</Avatar>
                    </Badge>
                  </ListItemAvatar>
                  <ListItemText
                    primary={c?.name || 'Unknown'}
                    primaryTypographyProps={{ fontWeight: c?.unread ? 'bold' : 'normal' }}
                    secondary={c?.lastMessage || c?.subject || ''}
                    secondaryTypographyProps={{ noWrap: true }}
                  />
                </ListItemButton>
              ))}
            </List>
          )}
        </Paper>
      ) : (
        <Paper
          sx={{
            bgcolor: theme.palette.background.alt,
            display: 'flex',
            flexDirection: 'column',
            height: '70vh',
          }}
        >
          <Box p={2} display="flex" alignItems="center" gap={1}>
            <Button size="small" onClick={() => setSelectedId(null)}>
              &larr; Back
            </Button>
            <Box>
              <Typography variant="h5" fontWeight="bold">
                {conversation?.name || 'Conversation'}
              </Typography>
              {conversation?.subject && (
                <Typography variant="body2" color="text.secondary">
                  {conversation.subject}
                </Typography>
              )}
            </Box>
          </Box>
          <Divider />

          <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
            {convLoading ? (
              <Box display="flex" justifyContent="center" p={4}>
                <CircularProgress size={28} />
              </Box>
            ) : messages.length === 0 ? (
              <Typography variant="body2" color="text.secondary" align="center" p={4}>
                No messages yet.
              </Typography>
            ) : (
              messages.map((m, idx) => {
                const isAdmin = m?.sender === 'admin';
                return (
                  <Box
                    key={m?._id || idx}
                    sx={{
                      display: 'flex',
                      justifyContent: isAdmin ? 'flex-end' : 'flex-start',
                      mb: 1.5,
                    }}
                  >
                    <Box
                      sx={{
                        maxWidth: '75%',
                        p: 1.25,
                        borderRadius: 2,
                        bgcolor: isAdmin
                          ? theme.palette.primary.main
                          : theme.palette.neutral?.[300] || theme.palette.grey[300],
                        color: isAdmin
                          ? theme.palette.primary.contrastText
                          : theme.palette.getContrastText(
                              theme.palette.neutral?.[300] || theme.palette.grey[300]
                            ),
                      }}
                    >
                      <Typography variant="caption" fontWeight="bold">
                        {m?.senderName || (isAdmin ? 'You' : 'Customer')}
                      </Typography>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {m?.body || ''}
                      </Typography>
                      <Typography variant="caption" sx={{ display: 'block', opacity: 0.7, mt: 0.5 }}>
                        {formatTime(m?.createdAt)}
                      </Typography>
                    </Box>
                  </Box>
                );
              })
            )}
          </Box>

          <Divider />
          <Box sx={{ display: 'flex', alignItems: 'center', p: 1.5, gap: 1 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Type a message..."
              value={body}
              onChange={(e) => setBody(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
            />
            <Button
              variant="contained"
              endIcon={sending ? <CircularProgress size={16} color="inherit" /> : <Send />}
              onClick={handleSend}
              disabled={sending || !body.trim()}
            >
              Send
            </Button>
          </Box>
        </Paper>
      )}
    </PageContainer>
  );
};

export default MessageView;
